import type * as React from "react";
import { cn } from "@/lib/utils";

export interface UnderlineTabOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

interface UnderlineTabsProps<T extends string> {
  options: UnderlineTabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * 밑줄형 탭 바. 탭이 폭을 균등하게 나눠 갖고 활성 탭만 2px 밑줄이 굵어진다.
 *
 * 알약형 `SegmentedTabs` 와 쓰는 자리가 다르다 — 이쪽은 상세 패널의 화면 전환처럼
 * 영역 전체를 가르는 자리에, `SegmentedTabs` 는 폼 필드 안의 좁은 선택에 쓴다.
 */
export function UnderlineTabs<T extends string>({
  options,
  value,
  onChange,
  className,
}: UnderlineTabsProps<T>) {
  return (
    <div
      role="tablist"
      className={cn(
        "flex w-full items-center border-b border-lines-normal",
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex flex-1 cursor-pointer items-center justify-center p-2.5 whitespace-nowrap outline-none",
              // 비활성 탭도 같은 두께의 투명 밑줄을 둬서 전환할 때 높이가 흔들리지 않게 한다
              "border-b-2",
              active
                ? "typo-body-2-semibold border-labels-primary text-labels-primary"
                : "typo-body-2-medium border-transparent text-labels-assistive hover:text-labels-secondary",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

UnderlineTabs.displayName = "UnderlineTabs";
